// Catálogo de densidades para MAT 3: infiere kg equivalentes de una partida BREEAM
// a partir de su descripción + unidad. Mantiene paridad con /tmp/mat3_density.py.
//
// Estrategia en dos capas:
// 1) Heurística geométrica (espesor cm en m², sección NxM cm en m).
// 2) Catálogo de palabras clave por familia (puertas, sanitarios, cerámica,
//    placas yeso, perfiles acero, pinturas, impermeabilizantes, etc.).
//
// Devuelve:
//   - número (kg/unidad) si encuentra heurística aplicable
//   - 'EXCLUDE' si la partida es aislante (NA8 manual → MAT 4)
//   - null si no encuentra match (caller decide fallback)

const BASE_DENSITY = { m3: 2400, t: 1000, kg: 1 }

function norm(s) {
  if (!s) return ''
  return s.normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase().trim()
}

// Patrones de aislamiento térmico/acústico — NA8 → se evalúa en MAT 4, no aquí
const AISLANTE_PATTERNS = [
  /\baislamiento\b/, /\baislante\b/,
  /\bxps\b/, /\beps\b/, /\bpoliestireno\b/,
  /\blana\s+(mineral|roca|vidrio|oveja)\b/,
  /\bpoliuretano\b/, /\bpir\b/,
  /\bdanopren\b/, /\bisover\b/, /\bursa\b/, /\bursalita\b/,
  /\bantiimpactos\b/, /\babsorbente\s+acust/,
]

export function isAislante(description) {
  const d = norm(description)
  return AISLANTE_PATTERNS.some(p => p.test(d))
}

// Catálogo de palabras clave: orden importante (las específicas van ANTES que las genéricas)
const KEYWORD_TABLE = [
  // Pinturas, barnices, tratamientos
  [/\b(pintura intumescente|proteccion.*fuego|protecc.*pasiva.*fuego)\b/, 1.5, ['m2']],
  [/\b(pintura|esmalte|barniz|imprimacion|tratamiento antioxi|recubrimiento)\b/, 0.5, ['m2']],
  // Impermeabilización, láminas
  [/\b(lamina|impermeabili|membrana|drenaje|epdm|tpo|geotextil|polietileno)\b/, 3.0, ['m2']],
  // Mortero, enfoscado
  [/\b(mortero|enfoscado|revoco|enlucido|monocapa|rasilla)\b/, 25.0, ['m2']],
  // Placa de yeso / Knauf / Pladur / Diamant
  [/\b(placa|pladur|placa de yeso|yeso laminado|diamant|aquapanel)\b/, 12.5, ['m2']],
  // Cerámica / gres / porcelánico
  [/\b(porcelanico|gres porcelanico|baldosa cera|azulejo|todatech|porcelanosa)\b/, 22.0, ['m2']],
  [/\bcera?mic\b/, 22.0, ['m2']],
  // Piedra natural
  [/\b(granito|marmol|piedra natural)\b/, 60.0, ['m2']],
  [/\bpizarra\b/, 30.0, ['m2']],
  // Madera, parquet
  [/\b(parquet|tarima|laminado madera)\b/, 15.0, ['m2']],
  // Vinilo, PVC
  [/\b(vinilo|pvc suelo|linoleo)\b/, 5.0, ['m2']],
  // Asfalto, adoquines
  [/\b(asfalto|aglomerado asf|mezcla bitumin)\b/, 100.0, ['m2']],
  [/\b(adoquin|baldosa hidraul)\b/, 100.0, ['m2']],
  // Vegetal, césped
  [/\b(cesped|jardin vegetal|hierba|sustrato)\b/, 30.0, ['m2']],

  // Específicos carpintería de puertas (antes que "puerta" genérico)
  [/\bprecerco.*(acero|galvaniz)/, 10.0, ['ud']],
  [/\bprecerco.*(madera|pino)/, 8.0, ['ud']],
  [/\bprecerco/, 9.0, ['ud']],
  [/\b(armazon|cassette)\b/, 40.0, ['ud']],
  // Puertas
  [/\bpuerta\s+exterior\b/, 80.0, ['ud']],
  [/\bpuerta\s+blindad/, 80.0, ['ud']],
  [/\bpuerta\s+garaje|basculante/, 150.0, ['ud']],
  [/\bpuerta\s+corredera/, 60.0, ['ud']],
  [/\bpuerta/, 50.0, ['ud']],
  // Ventanas, lucernarios
  [/\b(ventana|lucernario|vidriera|claraboya)\b/, 30.0, ['m2', 'ud']],
  // Sanitarios
  [/\b(inodoro|wc|cisterna)\b/, 30.0, ['ud']],
  [/\b(lavabo|lavamanos)\b/, 20.0, ['ud']],
  [/\b(plato de ducha|ducha)\b/, 30.0, ['ud']],
  [/\b(banera)\b/, 40.0, ['ud']],
  [/\bbidet\b/, 25.0, ['ud']],
  [/\burinario\b/, 25.0, ['ud']],
  [/\b(grifo|monomando|mezclador|griferia)\b/, 1.5, ['ud']],
  // Luminarias, aparatos eléctricos
  [/\b(luminaria|downlight|plafon|aplique|foco)\b/, 1.5, ['ud']],
  [/\b(enchufe|toma|interruptor|conmutador|pulsador)\b/, 0.2, ['ud']],
  // Trampillas, registros
  [/\b(trampilla|registro.*falso techo|alu-plac)\b/, 12.0, ['ud']],
  // Cubierta: drenaje
  [/\b(cazoleta|sumidero|imbornal)\b/, 0.5, ['ud']],
  [/\bgargola\b/, 5.0, ['ud']],
  // Celosías
  [/\b(celosia|persiana fija|persiana abatible)\b/, 30.0, ['ud']],
  // Falso techo especial
  [/\b(falso techo|techo registrable|techo desmonta).*(lana|fibra|organic)/, 15.0, ['m2']],
  [/\b(falso techo|techo registrable)\b/, 12.0, ['m2']],
  // Empanelado / paneles madera
  [/\b(empanelado|panelado|forro).*(madera|iroko|tablero)/, 30.0, ['m2']],
  [/\b(empanelado|panelado|tablero).*(finsa|fimapan|melamin|chapad)/, 10.0, ['m2']],
  [/\b(empanelado|panelado|forro)\b/, 12.0, ['m2']],
  // Remates / molduras
  [/\b(remate.*borde|foseado|cortinero|placa volada|moldura)\b/, 3.0, ['m']],
  // Peldaños piedra
  [/\b(peldano|huella).*(gabro|granito|marmol|piedra)/, 35.0, ['m']],
  [/\b(peldano|huella).*(porcelan|gres|cera?mic)/, 15.0, ['m']],
  // Marcos, herrajes
  [/\b(marco|premarco|herraje)\b/, 10.0, ['ud']],
  // Anclajes, pernos
  [/\b(perno.*anclaje|tornillo|hilti.*hsl|anclaje.*mecanico)\b/, 0.05, ['ud']],
  [/\bplaca\s+de\s+anclaje/, 5.0, ['ud']],
  // Refuerzos
  [/\b(refuerzo|cajeado|cuelgue)\b/, 10.0, ['ud']],
  // Bancos
  [/\bbanco\b/, 150.0, ['m', 'ud']],
  // Rodapiés
  [/\brodapie\b/, 1.4, ['m', 'ml']],
  // Carpintería aluminio
  [/\b(carpinteria aluminio|aluminio.*carp|muro cortina)\b/, 25.0, ['m2']],
  // Persianas
  [/\b(persiana|veneciana)\b/, 15.0, ['m2', 'ud']],
  // Cables, tuberías
  [/\bcable\b/, 0.3, ['m']],
  [/\b(tubo.*cobre|tubo.*acero)\b/, 1.0, ['m']],
  [/\b(tubo.*pvc|tuberia pvc|tuberia desague)\b/, 0.8, ['m']],
  // Perfiles acero
  [/\b(perfil ipe|perfil hea|perfil heb|perfil ipn)\b/, 25.0, ['m']],
  [/\b(tubo estructural|hueco|cuadrado.*acero)\b/, 5.0, ['m']],
  [/\b(corrugado|redondo|barra acero b\s*500)\b/, 1.5, ['m', 'kg']],
  [/\b(chapa|perfil acero conformado)\b/, 3.0, ['m']],
]

// Regex geométricas
const RE_ESPESOR_CM = /(\d{1,3})\s*\+?\s*(\d{0,3})\s*cm/i
const RE_SECCION_NxM = /(\d{1,3})\s*x\s*(\d{1,3})\s*cm/i
const RE_BLOQUE_NCM = /\bbloque\s+(?:hueco\s+)?(?:hormigon\s+)?(?:vibrado\s+)?(?:de\s+)?(\d{1,3})\s*cm\b/i

export function inferKgPerUnit(description, unit) {
  if (isAislante(description)) return 'EXCLUDE'
  const desc = norm(description)
  const u = (unit || '').toLowerCase().trim()
  if (u === 'm3' || u === 't' || u === 'kg') return BASE_DENSITY[u]

  // 1) Catálogo de palabras clave (orden importante)
  for (const [pat, factor, units] of KEYWORD_TABLE) {
    if (units.includes(u) && pat.test(desc)) return factor
  }

  // 2) Heurística geométrica para m² (fábrica/bloque/hormigón con espesor)
  if (u === 'm2') {
    let m = RE_BLOQUE_NCM.exec(desc)
    if (m) return parseInt(m[1], 10) * 24
    if (/\b(forjado|losa)\b/.test(desc)) {
      m = RE_ESPESOR_CM.exec(desc)
      if (m) return (parseInt(m[1], 10) + parseInt(m[2] || '0', 10)) * 24
    }
    if (/\bhormigon\b/.test(desc)) {
      m = RE_ESPESOR_CM.exec(desc)
      if (m) return (parseInt(m[1], 10) + parseInt(m[2] || '0', 10)) * 24
    }
    if (/\b(fabrica|murete)\b/.test(desc)) {
      m = RE_ESPESOR_CM.exec(desc) || RE_BLOQUE_NCM.exec(desc)
      if (m) return parseInt(m[1], 10) * 24
    }
  }

  // 3) Heurística para m (perfiles/dinteles)
  if (u === 'm' || u === 'ml') {
    const m = RE_SECCION_NxM.exec(desc)
    if (m) {
      const sec = parseInt(m[1], 10) * parseInt(m[2], 10)
      if (/\b(hormigon|pilarete|dintel|alfeizar|lama)\b/.test(desc)) return sec * 0.24
      if (/\b(acero|perfil)\b/.test(desc)) return sec * 0.785
    }
    if (/\bpeldano\b/.test(desc) && /\bhormigon\b/.test(desc)) return 510 * 0.24
  }

  return null
}
